/* eslint-disable react/require-default-props */
import { Box, Checkbox, Grid, Typography } from '@mui/material';
import { APIProvider } from '@vis.gl/react-google-maps';
import { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { LocationPrecision } from '../../../api/cep/types';
import { CoordinateEventData } from '../../../api/workOrders/types';
import { IconLocationOnMS, IconTaskAltMS } from '../../../constants/icons';
import { GlobalContext } from '../../../context/global';
import { formatDateAndTime } from '../../../helpers';
import { useStoragedJwt } from '../../../hooks/useDecodedJwt';
import useSnackbar from '../../../hooks/useSnackbar';
import { Constants } from './constants';
import CustomMap from './CustomMap';
import {
  BoldTypography,
  BoxContainer,
  CheckboxFormControl,
  CoordGrid,
  PinText,
  StyledTypography,
} from './styles';

interface StandardMapProps {
  searchMap?: boolean;
  address?: string;
  district?: string;
  city?: string;
  uf?: string;
  pinPlace: google.maps.LatLngLiteral;
  setPinPlace?: (place: google.maps.LatLngLiteral) => void;
  lastCoordinateEvent?: CoordinateEventData | null;
  checkLocation?: boolean;
  handleCheckLocation?: () => Promise<void>;
  checkLocationDate?: string;
  draggable?: boolean;
  showCoords?: boolean;
}

export default function StandardMap({
  searchMap,
  address,
  district,
  city,
  uf,
  pinPlace,
  setPinPlace,
  lastCoordinateEvent,
  checkLocation,
  handleCheckLocation,
  checkLocationDate,
  draggable = true,
  showCoords = false,
}: StandardMapProps): JSX.Element {
  const { isHiveOwner } = useContext(GlobalContext);
  const location = useLocation();
  const user = useStoragedJwt();
  const { handleSnackbar } = useSnackbar();
  const [mapSearchPrecision, setMapSearchPrecision] = useState<
    LocationPrecision | undefined
  >(undefined);

  useEffect(() => {
    setMapSearchPrecision(undefined);
  }, [searchMap]);

  return (
    <BoxContainer id="pin">
      <APIProvider
        apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string}
        libraries={['marker']}
      >
        <CustomMap
          searchMap={searchMap}
          address={address}
          district={district}
          city={city}
          uf={uf}
          pinPlace={pinPlace}
          setPinPlace={setPinPlace}
          onSearchPrecision={setMapSearchPrecision}
          onSearchError={() => handleSnackbar(Constants.search_error, true)}
          draggable={draggable}
        />
      </APIProvider>
      {mapSearchPrecision === 'approximate' && (
        <StyledTypography>{Constants.approximate_loc}</StyledTypography>
      )}
      {draggable ? (
        <>
          {showCoords && (
            <Grid container spacing={3}>
              <CoordGrid item xs={6}>
                <BoldTypography>{Constants.lat}</BoldTypography>
                <Typography>{pinPlace.lat}</Typography>
              </CoordGrid>
              <CoordGrid item xs={6}>
                <BoldTypography>{Constants.lng}</BoldTypography>
                <Typography>{pinPlace.lng}</Typography>
              </CoordGrid>
            </Grid>
          )}
          <PinText>{Constants.movePin}</PinText>
        </>
      ) : (
        <Box>
          {checkLocation && (
            <Box>
              {!isHiveOwner &&
                location.pathname.includes('inspection') &&
                (checkLocationDate ? (
                  <StyledTypography error="true">
                    {IconTaskAltMS}
                    Localização atualizada por {user?.user.name} em:{' '}
                    {formatDateAndTime(checkLocationDate)}
                  </StyledTypography>
                ) : (
                  <Box display="flex" justifyContent="space-between">
                    <StyledTypography>
                      {IconLocationOnMS}
                      {Constants.locPending}
                    </StyledTypography>
                    <CheckboxFormControl
                      label="confirmar localização"
                      control={<Checkbox color="primary" />}
                      onChange={handleCheckLocation}
                    />
                  </Box>
                ))}
              {lastCoordinateEvent &&
                (isHiveOwner || !location.pathname.includes('inspection')) &&
                (lastCoordinateEvent.user_checked ? (
                  <StyledTypography error="true">
                    {IconTaskAltMS}
                    Localização atualizada por {
                      lastCoordinateEvent?.user.name
                    }{' '}
                    em: {formatDateAndTime(lastCoordinateEvent?.created_at)}
                  </StyledTypography>
                ) : (
                  <StyledTypography>
                    {IconLocationOnMS}
                    {Constants.locPending}
                  </StyledTypography>
                ))}
            </Box>
          )}
        </Box>
      )}
    </BoxContainer>
  );
}
